import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useState } from 'react';
// @mui
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Tooltip from '@mui/material/Tooltip';
// routes
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
// _mock
import { _roles, _userList, USER_STATUS_OPTIONS } from 'src/_mock';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// components
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import { ConfirmDialog } from 'src/components/custom-dialog';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import {
  emptyRows,
  getComparator,
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSelectedAction,
  useTable,
} from 'src/components/table';
import UserTableFiltersResult from '../../support-tickets/supportTicket-table-filter';
import UserTableRow from './marketing-table-row';
import UserTableToolbar from './marketing-table-show-toolebar';
import { MarketingToolCreateModal } from './create-modal';
import { getRequest } from 'src/services';
import { endpoints } from 'src/utils/axios';
//

// ----------------------------------------------------------------------

const STATUS_OPTIONS = [ { value: 'all', label: 'All' }, ...USER_STATUS_OPTIONS ];

const TABLE_HEAD = [
  { id: 'name', label: 'ID' },
  { id: 'phoneNumber', label: 'Tool Name' },
  { id: 'company', label: 'Campaign Name' },
  { id: 'role', label: 'Target Audience' },
  { id: 'status', label: 'Date' },
  { id: 'status', label: 'Platform' },
  { id: 'status', label: 'Payment Method' },
  { id: 'status', label: 'Status' },
  { id: 'status', label: 'Actions' },
];

const defaultFilters = {
  name: '',
  role: [],
  status: 'all',
};

const TABLE_DATA = [
  {
    id: '001',
    phoneNumber: 'Hootsuite',
    company: 'Spring Launch',
    role: 'Social Media Managers',
    date: '2025-04-10',
    platform: 'Instagram',
    paymentMethod: 'Credit Card',
    status: 'Active',
  },
  {
    id: '002',
    phoneNumber: 'Canva Pro',
    company: 'Design Sprint',
    role: 'Graphic Designers',
    date: '2025-04-05',
    platform: 'Facebook',
    paymentMethod: 'PayPal',
    status: 'Pending',
  },
  {
    id: '003',
    phoneNumber: 'Mailchimp',
    company: 'Newsletter Blast',
    role: 'Email Marketers',
    date: '2025-03-30',
    platform: 'Email',
    paymentMethod: 'Debit Card',
    status: 'Completed',
  },
  {
    id: '004',
    phoneNumber: 'Buffer',
    company: 'Product Launch',
    role: 'Content Creators',
    date: '2025-04-01',
    platform: 'LinkedIn',
    paymentMethod: 'Stripe',
    status: 'Failed',
  },
  {
    id: '005',
    phoneNumber: 'HubSpot',
    company: 'Lead Generation',
    role: 'Sales Teams',
    date: '2025-04-08',
    platform: 'Website',
    paymentMethod: 'Credit Card',
    status: 'Active',
  },
];

// ----------------------------------------------------------------------

export default function MarketigToolTable() {
  const table = useTable();

  const settings = useSettingsContext();

  const router = useRouter();
  const add = useBoolean();
  const confirm = useBoolean();

  const [ loading, setLoading ] = useState(false);
  const [ tableData, setTableData ] = useState(TABLE_DATA);
  const [ selectedItem, setSelectedItem ] = useState({});

  const [ filters, setFilters ] = useState(defaultFilters);

  const dataFiltered = applyFilter({
    inputData: tableData,
    comparator: getComparator(table.order, table.orderBy),
    filters,
  });

  const dataInPage = dataFiltered.slice(
    table.page * table.rowsPerPage,
    table.page * table.rowsPerPage + table.rowsPerPage
  );




  useEffect(() => {
    getUsers();
  }, [ table.page, table.rowsPerPage ]);

  const getUsers = async () => {
    setLoading(true)

    const data = await getRequest(`${endpoints.admin_dashboard.marketing_management.marketing_tools.get_all_compains}`);
    if (data) {
      setTableData(data || []);
    }
    setLoading(false)

  };


  const denseHeight = table.dense ? 52 : 72;

  const canReset = !isEqual(defaultFilters, filters);

  const notFound = (!dataFiltered.length && canReset) || !dataFiltered.length;

  const handleFilters = useCallback(
    (name, value) => {
      table.onResetPage();
      setFilters((prevState) => ({
        ...prevState,
        [ name ]: value,
      }));
    },
    [ table ]
  );

  const handleDeleteRow = useCallback(
    (id) => {
      const deleteRow = tableData.filter((row) => row.id !== id);
      setTableData(deleteRow);

      table.onUpdatePageDeleteRow(dataInPage.length);
    },
    [ dataInPage.length, table, tableData ]
  );

  const handleDeleteRows = useCallback(() => {
    const deleteRows = tableData.filter((row) => !table.selected.includes(row.id));
    setTableData(deleteRows);

    table.onUpdatePageDeleteRows({
      totalRows: tableData.length,
      totalRowsInPage: dataInPage.length,
      totalRowsFiltered: dataFiltered.length,
    });
  }, [ dataFiltered.length, dataInPage.length, table, tableData ]);

  const handleEditRow = useCallback(
    (id) => {
      router.push(paths.dashboard.user.edit(id));
    },
    [ router ]
  );

  const handleFilterStatus = useCallback(
    (event, newValue) => {
      handleFilters('status', newValue);
    },
    [ handleFilters ]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const handleclose = () => {
    add.onFalse();
    setSelectedItem({});
  };
  return (
    <>
      <CustomBreadcrumbs
        heading="Marketing Tools"
        links={ [ { name: '', href: paths.dashboard.root } ] }
        action={
          <Button
            variant="contained"
            startIcon={ <Iconify icon="mingcute:add-line" /> }
            onClick={ () => add.onTrue() }
          >
            Create
          </Button>
        }
        sx={ {
          mb: { xs: 3, md: 5 },
        } }
      />

      <Card>
        {/* <Tabs
                    value={ filters.status }
                    onChange={ handleFilterStatus }
                    sx={ {
                        px: 2.5,
                        boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[ 500 ], 0.08)}`,
                    } }
                >
                    { STATUS_OPTIONS.map((tab) => (
                        <Tab
                            key={ tab.value }
                            iconPosition="end"
                            value={ tab.value }
                            label={ tab.label }
                            icon={
                                <Label
                                    variant={
                                        ((tab.value === 'all' || tab.value === filters.status) && 'filled') || 'soft'
                                    }
                                    color={
                                        (tab.value === 'active' && 'success') ||
                                        (tab.value === 'pending' && 'warning') ||
                                        (tab.value === 'banned' && 'error') ||
                                        'default'
                                    }
                                >
                                    { tab.value === 'all' && _userList.length }
                                    { tab.value === 'active' &&
                                        _userList.filter((user) => user.status === 'active').length }

                                    { tab.value === 'pending' &&
                                        _userList.filter((user) => user.status === 'pending').length }
                                    { tab.value === 'banned' &&
                                        _userList.filter((user) => user.status === 'banned').length }
                                    { tab.value === 'rejected' &&
                                        _userList.filter((user) => user.status === 'rejected').length }
                                </Label>
                            }
                        />
                    )) }
                </Tabs> */}

        <UserTableToolbar
          filters={ filters }
          onFilters={ handleFilters }
          //
          roleOptions={ _roles }
        />

        { canReset && (
          <UserTableFiltersResult
            filters={ filters }
            onFilters={ handleFilters }
            //
            onResetFilters={ handleResetFilters }
            //
            results={ dataFiltered.length }
            sx={ { p: 2.5, pt: 0 } }
          />
        ) }

        <TableContainer sx={ { position: 'relative', overflow: 'unset' } }>
          <TableSelectedAction
            dense={ table.dense }
            numSelected={ table.selected.length }
            rowCount={ tableData.length }
            onSelectAllRows={ (checked) =>
              table.onSelectAllRows(
                checked,
                tableData.map((row) => row.id)
              )
            }
            action={
              <Tooltip title="Delete">
                <IconButton color="primary" onClick={ confirm.onTrue }>
                  <Iconify icon="solar:trash-bin-trash-bold" />
                </IconButton>
              </Tooltip>
            }
          />

          <Scrollbar>
            <Table size={ table.dense ? 'small' : 'medium' } sx={ { minWidth: 960 } }>
              <TableHeadCustom
                order={ table.order }
                orderBy={ table.orderBy }
                headLabel={ TABLE_HEAD }
                rowCount={ tableData.length }
                numSelected={ table.selected.length }
                onSort={ table.onSort }
                onSelectAllRows={ (checked) =>
                  table.onSelectAllRows(
                    checked,
                    tableData.map((row) => row.id)
                  )
                }
              />

              <TableBody>
                { dataFiltered
                  .slice(
                    table.page * table.rowsPerPage,
                    table.page * table.rowsPerPage + table.rowsPerPage
                  )
                  .map((row) => (
                    <UserTableRow
                      key={ row.id }
                      row={ row }
                      selected={ table.selected.includes(row.id) }
                      onSelectRow={ () => table.onSelectRow(row.id) }
                      onDeleteRow={ () => handleDeleteRow(row.id) }
                      onEditRow={ () => handleEditRow(row.id) }
                    />
                  )) }

                <TableEmptyRows
                  height={ denseHeight }
                  emptyRows={ emptyRows(table.page, table.rowsPerPage, tableData.length) }
                />

                <TableNoData notFound={ notFound } />
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={ dataFiltered.length }
          page={ table.page }
          rowsPerPage={ table.rowsPerPage }
          onPageChange={ table.onChangePage }
          onRowsPerPageChange={ table.onChangeRowsPerPage }
          //
          dense={ table.dense }
          onChangeDense={ table.onChangeDense }
        />
      </Card>
      <MarketingToolCreateModal
        open={ add.value }
        onClose={ handleclose }
        // reload={ () => getCostCodes() }
        currentItem={ selectedItem }
      />
      <ConfirmDialog
        open={ confirm.value }
        onClose={ confirm.onFalse }
        title="Delete"
        content={
          <>
            Are you sure want to delete <strong> { table.selected.length } </strong> items?
          </>
        }
        action={
          <Button
            variant="contained"
            color="error"
            onClick={ () => {
              handleDeleteRows();
              confirm.onFalse();
            } }
          >
            Delete
          </Button>
        }
      />
    </>
  );
}

// ----------------------------------------------------------------------

function applyFilter({ inputData, comparator, filters }) {
  const { name, status, role } = filters;

  const stabilizedThis = inputData.map((el, index) => [ el, index ]);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[ 0 ], b[ 0 ]);
    if (order !== 0) return order;
    return a[ 1 ] - b[ 1 ];
  });

  inputData = stabilizedThis.map((el) => el[ 0 ]);

  if (name) {
    inputData = inputData.filter(
      (user) => user.name.toLowerCase().indexOf(name.toLowerCase()) !== -1
    );
  }

  if (status !== 'all') {
    inputData = inputData.filter((user) => user.status === status);
  }

  if (role.length) {
    inputData = inputData.filter((user) => role.includes(user.role));
  }

  return inputData;
}
